import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Card,
  Chip,
  DataTable,
  Field,
  Input,
  Modal,
  PageHead,
  Pager,
  SearchInput,
  StateBlock,
  Textarea,
  useToast,
  type Column,
} from '@w7/ui'
import { ApiError } from '@/api/client'
import { count, EMPTY } from '@/lib/format'
import type { BlockedDeviceRow } from './blockedDeviceTypes'
import { useBlockedDevices } from './useBlockedDevices'
import { useDeleteBlockedDevice, useSaveBlockedDevice } from './useBlockedDeviceMutations'

/**
 * Blocked Device Ids — Winner All Logs (Master only).
 *
 * Filters/columns match winner-golive blocked-device-ids.html.
 * List: Agent-API `playersWithBlockedDevices`.
 * Add / Delete: Agent-API `BlockedIp/saveBlockedIp` + `deleteBlockedIp`.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function BlockedDeviceIdsPage() {
  const toast = useToast()
  const [page, setPage] = useState(1)
  const [deviceId, setDeviceId] = useState('')
  const [player, setPlayer] = useState('')
  const [applied, setApplied] = useState({ deviceId: '', player: '' })
  const [addOpen, setAddOpen] = useState(false)
  const [newDeviceId, setNewDeviceId] = useState('')
  const [newComment, setNewComment] = useState('')
  const [pendingDelete, setPendingDelete] = useState<BlockedDeviceRow | null>(null)

  const { data, isLoading } = useBlockedDevices({
    page,
    pageSize: PAGE_SIZE,
    deviceId: applied.deviceId,
    player: applied.player,
  })
  const saveMutation = useSaveBlockedDevice()
  const deleteMutation = useDeleteBlockedDevice()

  const columns = useMemo<Column<BlockedDeviceRow>[]>(
    () => [
      {
        id: 'index',
        header: '#',
        align: 'right',
        numeric: true,
        cell: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
      },
      {
        id: 'deviceId',
        header: 'Device Id',
        cell: (row) => row.deviceId || EMPTY,
      },
      {
        id: 'comment',
        header: 'Comment',
        cell: (row) => row.comment || EMPTY,
      },
      {
        id: 'playerCount',
        header: 'Duplicate Count',
        align: 'right',
        numeric: true,
        cell: (row) => row.playerCount,
      },
      {
        id: 'players',
        header: 'Players',
        cell: (row) => (
          <span className="w7-row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {row.players.map((p) => (
              <Chip
                key={`${row.id}-${p.username}`}
                size="sm"
                tone={p.accessLevel === 'Player' ? 'loss' : 'profit'}
              >
                {p.username}
                {p.siteUrl ? ` · ${p.siteUrl}` : ''}
              </Chip>
            ))}
          </span>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        cell: (row) => (
          <Button
            variant="danger"
            size="sm"
            disabled={!row.id || deleteMutation.isPending}
            onClick={() => setPendingDelete(row)}
          >
            Delete
          </Button>
        ),
      },
    ],
    [page, deleteMutation.isPending],
  )

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  const search = () => {
    setPage(1)
    setApplied({ deviceId: deviceId.trim(), player: player.trim() })
  }

  const resetAddForm = () => {
    setAddOpen(false)
    setNewDeviceId('')
    setNewComment('')
  }

  const closeAdd = () => {
    if (saveMutation.isPending) return
    resetAddForm()
  }

  const onSave = async () => {
    try {
      await saveMutation.mutateAsync({
        deviceId: newDeviceId,
        comment: newComment,
      })
      toast.push({
        tone: 'success',
        message: 'ID Blocked successfully.',
        detail: 'Blocked',
      })
      resetAddForm()
    } catch (err) {
      toast.push({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Error in Device Blocked',
        detail: 'Blocked',
      })
    }
  }

  const onConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteMutation.mutateAsync({
        id: pendingDelete.id,
        deviceId: pendingDelete.deviceId,
        comment: pendingDelete.comment,
      })
      toast.push({
        tone: 'success',
        message: 'Device ID deleted successfully.',
      })
      setPendingDelete(null)
    } catch (err) {
      const detail =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : undefined
      toast.push({
        tone: 'error',
        message: 'There was an error while deleting Device ID',
        detail: detail ?? 'Device ID deleting error',
      })
    }
  }

  const canSave = newDeviceId.trim().length > 0 && !saveMutation.isPending

  return (
    <>
      <PageHead
        title="Blocked Device Ids"
        subtitle="Blocked device ids and the accounts still tied to them — Master All Logs from Winner"
      />

      <Card
        title="Filters"
        subtitle="Search by device id or player · Red = Player, green = Master"
        actions={
          <span className="w7-row" style={{ gap: 8 }}>
            <Button variant="primary" onClick={search}>
              Search
            </Button>
            <Button variant="secondary" onClick={() => setAddOpen(true)}>
              Add New Device Id
            </Button>
          </span>
        }
      >
        <div className="w7-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="Device Id"
              value={deviceId}
              onChange={setDeviceId}
              placeholder="Search device id…"
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="Player"
              value={player}
              onChange={setPlayer}
              placeholder="Search player…"
            />
          </div>
        </div>
      </Card>

      <Card
        flush
        title="Blocked devices"
        subtitle={isLoading ? 'Loading' : `${count(total)} in this list`}
        footer={
          <Pager
            page={data?.page ?? page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Blocked device ids"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={6}
          empty={
            <StateBlock icon="users" title="No data found">
              No blocked device ids match these filters.
            </StateBlock>
          }
        />
      </Card>

      <Modal
        open={addOpen}
        onClose={closeAdd}
        title="Create Blocked Device"
        subtitle="Blocks this device id for the console tree"
        closeDisabled={saveMutation.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button variant="secondary" onClick={closeAdd} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!canSave}
              busy={saveMutation.isPending}
              onClick={() => void onSave()}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
          <Field label="Device Id" required>
            {({ id }) => (
              <Input
                id={id}
                value={newDeviceId}
                onChange={(event) => setNewDeviceId(event.target.value)}
                placeholder="Enter Device Id"
                disabled={saveMutation.isPending}
              />
            )}
          </Field>
          <Field label="Comment">
            {({ id }) => (
              <Textarea
                id={id}
                value={newComment}
                onChange={(event) => setNewComment(event.target.value)}
                placeholder="Enter Comments"
                disabled={saveMutation.isPending}
              />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={pendingDelete !== null}
        onClose={() => {
          if (!deleteMutation.isPending) setPendingDelete(null)
        }}
        title="Delete Device Id"
        subtitle={
          pendingDelete?.deviceId
            ? 'Are you sure you want to Delete Device ID ' + pendingDelete.deviceId + '?'
            : 'Are you sure you want to Delete Device ID?'
        }
        closeDisabled={deleteMutation.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button
              variant="secondary"
              disabled={deleteMutation.isPending}
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              busy={deleteMutation.isPending}
              disabled={deleteMutation.isPending}
              onClick={() => void onConfirmDelete()}
            >
              Delete
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          This removes the block from the list. Accounts that still share the device id stay
          unchanged.
        </p>
      </Modal>
    </>
  )
}
